import React, { useState, useEffect } from 'react';
import { X, Check, ArrowRight, Mail, Phone, User, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useFormSubmit } from '../hooks/useFormSubmit';
import countryCodes from '../utils/countryCodes';

const ServicePopup = ({ isOpen, onClose, serviceTitle, serviceDescription }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  
  // Use centralized form submission hook
  const { isSubmitting, formStatus, handleSubmit: submitForm, geoData } = useFormSubmit();

  // Auto-select country based on geolocation
  useEffect(() => {
    if (geoData?.isSuccess && geoData?.data?.countryCode) {
      const matchingCountry = countryCodes.find(
        country => country.country === geoData.data.countryName
      );
      
      if (matchingCountry) {
        setFormData(prev => ({
          ...prev,
          countryCode: matchingCountry.code
        }));
      }
    }
  }, [geoData]);

  // Show success status from centralized hook
  useEffect(() => {
    if (formStatus.type === 'success') {
      setSubmitted(true);
      // Close popup after showing success message briefly
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', countryCode: '+1', message: '' });
        onClose();
      }, 2000);
    }
  }, [formStatus, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitForm(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Popup Container */}
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-[#1a1a1a] via-[#2C3E50] to-[#1a1a1a] rounded-2xl shadow-2xl border-2 border-[#F4D03F] overflow-hidden animate-slideUp">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F4D03F] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37] opacity-5 rounded-full blur-3xl"></div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#000000]/50 hover:bg-[#F4D03F]/20 transition-all duration-300 group"
        >
          <X className="text-[#F4D03F] group-hover:rotate-90 transition-transform duration-300" size={20} />
        </button>

        {/* Content */}
        <div className="relative p-5 sm:p-6 md:p-8">
          {!submitted ? (
            <>
              {/* Header */}
              <div className="text-center mb-4 sm:mb-5">
                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-[#F4D03F]/10 border border-[#F4D03F]/30">
                  <div className="w-2 h-2 bg-[#F4D03F] rounded-full animate-pulse"></div>
                  <span className="text-[#F4D03F] text-xs font-semibold uppercase tracking-wider">Let's Begin Today</span>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F4D03F] mb-2 uppercase">
                  Let's Make It Happen
                </h2>
                
                <p className="text-[#dfddd6] text-sm sm:text-base max-w-xl mx-auto">
                  Share your vision below and our expert team will bring it to life with personalized guidance every step.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {/* Name */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F4D03F]">
                    <User size={18} />
                  </div>
                  <Input
                    type="text"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="pl-12 bg-[#000000] border-2 border-[#3f4816] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-11 text-sm sm:text-base rounded-xl transition-all duration-300 hover:border-[#F8D26B]"
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F4D03F]">
                    <Mail size={18} />
                  </div>
                  <Input
                    type="email"
                    placeholder="Your Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="pl-12 bg-[#000000] border-2 border-[#3f4816] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-11 text-sm sm:text-base rounded-xl transition-all duration-300 hover:border-[#F8D26B]"
                  />
                </div>

                {/* Country Display from Geolocation */}
                {geoData?.isSuccess && geoData?.data?.countryName && (
                  <div className="bg-[#2C3E50]/30 border-2 border-[#F4D03F]/30 rounded-xl p-3 flex items-center gap-2 hidden">
                    <MapPin className="text-[#F4D03F]" size={18} />
                    <span className="text-[#ffffff] text-sm">
                      Detected Location: <span className="text-[#F4D03F] font-semibold">{geoData.data.countryName}</span>
                    </span>
                  </div>
                )}

                {/* Phone with Country Code */}
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({...formData, countryCode: e.target.value})}
                    aria-label="Country Code"
                    className="bg-[#000000] border-2 border-[#3f4816] text-[#ffffff] rounded-xl px-3 py-3 text-sm sm:text-base focus:outline-none focus:border-[#F4D03F] transition-all duration-300 hover:border-[#F8D26B]"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.country} {country.code}
                      </option>
                    ))}
                  </select>
                  <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F4D03F]">
                      <Phone size={18} />
                    </div>
                    <Input
                      type="tel"
                      placeholder="(123) 456-7890"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="pl-12 bg-[#000000] border-2 border-[#3f4816] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-11 text-sm sm:text-base rounded-xl transition-all duration-300 hover:border-[#F8D26B]"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Textarea
                    placeholder="Tell us about your project requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={4}
                    className="bg-[#000000] border-2 border-[#3f4816] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] text-sm sm:text-base rounded-xl transition-all duration-300 hover:border-[#F8D26B] resize-none"
                  />
                </div>

                {/* Status Messages */}
                {formStatus.message && (
                  <div className={`p-4 rounded-xl text-center text-sm sm:text-base ${
                    formStatus.type === 'success' 
                      ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50' 
                      : 'bg-red-500/20 text-red-400 border-2 border-red-500/50'
                  }`}>
                    {formStatus.message}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 sm:h-14 bg-[#F4D03F] text-[#0B0B0B] hover:bg-[#F8D26B] font-bold text-sm sm:text-base rounded-xl shadow-lg hover:shadow-[0_8px_30px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Button>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 pt-4 text-[#888680] text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-[#F4D03F]" />
                    <span>24-Hour Response</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-[#F4D03F]" />
                    <span>Free Consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-[#F4D03F]" />
                    <span>No Obligation</span>
                  </div>
                </div>
              </form>
            </>
          ) : (
            // Success State
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <Check size={32} className="text-[#F4D03F]" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4 uppercase">
                Thank You!
              </h3>
              <p className="text-[#dfddd6] text-base sm:text-lg mb-2">
                Your inquiry has been received successfully.
              </p>
              <p className="text-[#888680] text-sm sm:text-base">
                Our team will contact you within 24 hours.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ServicePopup;
